import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { useAuth } from "../hooks/useAuthStore";
import { useStore } from "../store/useStore";
import { supabase } from "../lib/supabase";
import type { ITask, ITaskCompletion } from "../types";
import {
  Check,
  CheckCircle2,
  ClipboardList,
  Pencil,
  SearchX,
  Trash2,
} from "lucide-react";
import { PointsBadge } from "../components/PointsBadge";
import { ZFrequencyUnit } from "../types/zod-schemas";
import { getDifficultyLabel } from "../logic/display-helpers";
import { EmptyState } from "../components/EmptyState";
import { PageHeader } from "../components/ui/PageHeader";
import { Button, PrimaryButton } from "../components/ui/Button";
import { Badge } from "../components/ui/Badge";
import { Card } from "../components/ui/Card";
import { RoomIcon } from "../components/ui/RoomIcon";
import { Skeleton } from "../components/ui/Skeleton";
import { cn } from "../lib/utils";
import { useDialogA11y } from "../hooks/useDialogA11y";

export default function TaskDetailPage() {
  const history = useHistory();
  const location = useLocation();
  const { areaId, taskId } = useParams<{ areaId: string; taskId: string }>();
  const { user } = useAuth();

  // Check mode from URL params
  const urlParams = new URLSearchParams(location.search);
  const mode = urlParams.get("mode");
  const isEditMode = mode === "edit";

  // Complete task state
  const [isCompleting, setIsCompleting] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [completionNotes, setCompletionNotes] = useState("");
  const [completionDate, setCompletionDate] = useState("");

  // Task state
  const [task, setTask] = useState<ITask | null>(null);
  const [taskCompletions, setTaskCompletions] = useState<ITaskCompletion[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Form state for edit mode
  const [taskName, setTaskName] = useState("");
  const [points, setPoints] = useState(2);
  const [frequencyNumber, setFrequencyNumber] = useState(1);
  const [frequencyUnit, setFrequencyUnit] = useState<
    "days" | "weeks" | "months" | "years"
  >("weeks");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [isHighPriority, setIsHighPriority] = useState(false);
  const [assignedToUserId, setAssignedToUserId] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [householdMembers, setHouseholdMembers] = useState<Array<{ uuid: string; name: string; role: string }>>([]);

  // Get data from Zustand store
  const areas = useStore((state) => state.areas);
  const updateTaskStore = useStore((state) => state.updateTask);
  const deleteTaskStore = useStore((state) => state.deleteTask);
  const completeTask = useStore((state) => state.completeTask);
  const currentHouseholdId = useStore((state) => state.currentHouseholdId);
  const userHouseholds = useStore((state) => state.userHouseholds);

  // Check if current user is a parent (only parents can assign tasks)
  const currentUserHousehold = userHouseholds.find(
    (uh) => uh.household_uuid === currentHouseholdId && uh.user_uuid === user?.id
  );
  const isParent = currentUserHousehold?.role === "parent";

  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch task data on mount
  useEffect(() => {
    const fetchTaskData = async () => {
      if (!taskId) return;

      setIsLoading(true);
      try {
        // Fetch task
        const { data: taskData, error: taskError } = await supabase
          .from("tasks")
          .select("*")
          .eq("uuid", taskId)
          .single();

        if (taskError || !taskData) {
          console.error("Error fetching task:", taskError);
          setTask(null);
        } else {
          setTask(taskData);
        }

        // Fetch task completions
        const { data: completions, error: completionsError } = await supabase
          .from("task_completions")
          .select("*")
          .eq("task_uuid", taskId)
          .order("completed_at", { ascending: false });

        if (!completionsError && completions) {
          setTaskCompletions(completions);
        }
      } catch (error) {
        console.error("Error in fetchTaskData:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaskData();
  }, [taskId]);

  // Set initial form values when task loads (for edit mode)
  useEffect(() => {
    if (task && isEditMode) {
      setTaskName(task.name);
      setPoints(task.points);
      setFrequencyNumber(task.frequency_number);
      setFrequencyUnit(task.frequency_unit);
      setSelectedAreaId(task.area_uuid || "");
      setIsHighPriority(task.is_high_priority || false);
      setAssignedToUserId(task.default_assigned_to_user_uuid || "");
    }
  }, [task, isEditMode]);

  // Fetch household members for the assignee selector
  useEffect(() => {
    if (!isEditMode || !isParent || !currentHouseholdId) return;

    const fetchMembers = async () => {
      const { data, error } = await supabase
        .from("user_households")
        .select("user_uuid, role, users:user_uuid(uuid, name)")
        .eq("household_uuid", currentHouseholdId);

      if (!error && data) {
        const members = data
          .filter((m): m is typeof m & { users: { uuid: string; name: string } } =>
            m.users !== null && typeof m.users === "object"
          )
          .map((m) => ({
            uuid: m.users.uuid,
            name: m.users.name,
            role: m.role,
          }));
        setHouseholdMembers(members);
      }
    };

    fetchMembers();
  }, [isEditMode, isParent, currentHouseholdId]);

  // Task history is already sorted from the query
  const taskHistory = taskCompletions;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskName.trim() || !selectedAreaId) return;

    setIsUpdating(true);
    try {
      const updated = await updateTaskStore(taskId, {
        name: taskName.trim(),
        points,
        frequency_number: frequencyNumber,
        frequency_unit: frequencyUnit,
        area_uuid: selectedAreaId,
        is_high_priority: isHighPriority,
        default_assigned_to_user_uuid: assignedToUserId || null,
      });
      // Reflect the saved values in view mode immediately
      if (updated) setTask(updated);
      setIsUpdating(false);

      // Navigate back to view mode
      history.push(`/areas/${areaId}/tasks/${taskId}?mode=view`);
    } catch (error) {
      console.error("Error updating task:", error);
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTaskStore(taskId);
      history.push(`/areas/${areaId}`);
    } catch (error) {
      console.error("Error deleting task:", error);
      setIsDeleting(false);
    }
  };

  const toggleMode = () => {
    const newMode = isEditMode ? "view" : "edit";
    history.push(`/areas/${areaId}/tasks/${taskId}?mode=${newMode}`);
  };

  const closeCompleteModal = useCallback(() => {
    setShowCompleteModal(false);
    setCompletionNotes("");
    setCompletionDate("");
  }, []);
  const closeDeleteConfirm = useCallback(() => setShowDeleteConfirm(false), []);
  const completeDialogFocusRef = useRef<HTMLInputElement>(null);
  const deleteDialogFocusRef = useRef<HTMLButtonElement>(null);
  useDialogA11y(showCompleteModal, closeCompleteModal, completeDialogFocusRef);
  useDialogA11y(showDeleteConfirm, closeDeleteConfirm, deleteDialogFocusRef);

  const handleConfirmComplete = async () => {
    if (!user?.id) return;
    setIsCompleting(true);
    try {
      // Build completedAt from the date picker, or undefined for "now"
      const completedAt = completionDate
        ? new Date(completionDate + "T12:00:00").toISOString()
        : undefined;
      const completion = await completeTask(taskId, completionNotes, completedAt);
      if (completion) {
        // Update local UI state without reload
        setTaskCompletions((prev) => [completion, ...prev]);
        setTask((prev) =>
          prev ? { ...prev, last_completed_at: completion.completed_at } : prev
        );
      }
      closeCompleteModal();
    } catch (error) {
      console.error("Error completing task:", error);
    } finally {
      setIsCompleting(false);
    }
  };

  if (isLoading) {
    return (
      <IonPage>
        <PageHeader title="Task" backHref={`/areas/${areaId}`} />
        <IonContent>
          <div className="max-w-lg mx-auto px-4 pt-2 space-y-4" role="status" aria-label="Loading task...">
            <Skeleton className="h-48 rounded-3xl" />
            <Skeleton className="h-14 rounded-full" />
            <Skeleton className="h-20 rounded-2xl" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!task) {
    return (
      <IonPage>
        <PageHeader title="Task" backHref={`/areas/${areaId}`} />
        <IonContent>
          <EmptyState
            icon={SearchX}
            title="Task not found"
            description="This task may have been deleted or moved."
            action={{ label: "Back to area", onClick: () => history.push(`/areas/${areaId}`) }}
          />
        </IonContent>
      </IonPage>
    );
  }

  const isCompletedToday =
    !!task.last_completed_at &&
    new Date(task.last_completed_at).toDateString() ===
      new Date().toDateString();

  const taskArea = areas.find((a) => a.uuid === task.area_uuid);

  const formatShortDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });

  return (
    <IonPage>
      <PageHeader
        title={isEditMode ? "Edit Task" : task.name}
        backHref={`/areas/${areaId}`}
        actions={
          <Button
            variant={isEditMode ? "secondary" : "soft"}
            size="sm"
            onClick={toggleMode}
            leadingIcon={
              isEditMode ? undefined : <Pencil className="w-4 h-4" aria-hidden="true" />
            }
            className="mr-2 min-h-[40px]"
          >
            {isEditMode ? "Cancel" : "Edit"}
          </Button>
        }
      />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 pt-2 pb-28 text-foreground">
          {isEditMode ? (
            // Edit Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="edit-task-name" className="field-label">
                  Task Name
                </label>
                <input
                  id="edit-task-name"
                  type="text"
                  value={taskName}
                  onChange={(e) => setTaskName(e.target.value)}
                  className="input-field"
                  placeholder="e.g., Vacuum living room"
                  required
                />
              </div>

              <fieldset>
                <legend className="field-label">Points (Difficulty)</legend>
                <div className="flex gap-2" role="radiogroup" aria-label="Points">
                  {[1, 2, 3, 4, 5].map((value) => {
                    const selected = points === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => setPoints(value)}
                        className={cn(
                          "flex-1 min-h-[48px] rounded-2xl font-bold transition-colors",
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          selected
                            ? "bg-primary text-primary-foreground shadow-glow-cyan"
                            : "bg-surface text-muted-foreground hover:bg-surface-raised"
                        )}
                      >
                        {value}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  1 = Very Easy, 5 = Very Hard · {getDifficultyLabel(points)}
                </p>
              </fieldset>

              <fieldset>
                <legend className="field-label">Frequency</legend>
                <div className="flex gap-2">
                  <input
                    type="number"
                    aria-label="Frequency number"
                    value={frequencyNumber}
                    onChange={(e) =>
                      setFrequencyNumber(
                        Math.max(1, parseInt(e.target.value) || 1)
                      )
                    }
                    className="input-field w-24"
                    min="1"
                    required
                  />
                  <select
                    aria-label="Frequency unit"
                    value={frequencyUnit}
                    onChange={(e) => {
                      try {
                        const value = ZFrequencyUnit.parse(e.target.value);
                        setFrequencyUnit(value);
                      } catch {
                        // Invalid frequency unit, ignore
                      }
                    }}
                    className="input-field flex-1"
                  >
                    <option value="days">
                      day{frequencyNumber > 1 ? "s" : ""}
                    </option>
                    <option value="weeks">
                      week{frequencyNumber > 1 ? "s" : ""}
                    </option>
                    <option value="months">
                      month{frequencyNumber > 1 ? "s" : ""}
                    </option>
                    <option value="years">
                      year{frequencyNumber > 1 ? "s" : ""}
                    </option>
                  </select>
                </div>
              </fieldset>

              <div>
                <label htmlFor="edit-task-area" className="field-label">
                  Area
                </label>
                <select
                  id="edit-task-area"
                  value={selectedAreaId}
                  onChange={(e) => setSelectedAreaId(e.target.value)}
                  className="input-field"
                  required
                >
                  <option value="">Select an area</option>
                  {areas.map((area) => (
                    <option key={area.uuid} value={area.uuid}>
                      {area.name}
                    </option>
                  ))}
                </select>
              </div>

              <label className="flex items-center gap-3 p-4 card-surface cursor-pointer">
                <input
                  type="checkbox"
                  checked={isHighPriority}
                  onChange={(e) => setIsHighPriority(e.target.checked)}
                  className="w-5 h-5 rounded accent-primary"
                />
                <span>
                  <span className="block text-foreground font-semibold">
                    High Priority
                  </span>
                  <span className="block text-sm text-muted-foreground">
                    Prevents this task from going over 100% dirty
                  </span>
                </span>
              </label>

              {isParent && householdMembers.length > 0 && (
                <div>
                  <label htmlFor="edit-task-assignee" className="field-label">
                    Assign To
                  </label>
                  <select
                    id="edit-task-assignee"
                    value={assignedToUserId}
                    onChange={(e) => setAssignedToUserId(e.target.value)}
                    className="input-field"
                  >
                    <option value="">Unassigned</option>
                    {householdMembers.map((member) => (
                      <option key={member.uuid} value={member.uuid}>
                        {member.name} ({member.role})
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-2">
                    Default assignee for daily task allocation
                  </p>
                </div>
              )}

              <PrimaryButton
                type="submit"
                disabled={isUpdating || !taskName.trim()}
              >
                {isUpdating ? "Updating..." : "Update Task"}
              </PrimaryButton>
            </form>
          ) : (
            // View Mode
            <div className="space-y-6">
              {/* Task Details Card */}
              <Card padding="lg">
                <div className="flex items-start gap-4 mb-5">
                  <RoomIcon name={taskArea?.name} size="md" />
                  <div className="flex-1 min-w-0">
                    <h2 className="text-2xl font-extrabold tracking-tight text-foreground mb-1">
                      {task.name}
                    </h2>
                    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                      <span>
                        Every {task.frequency_number} {task.frequency_unit}
                      </span>
                      {task.is_high_priority && (
                        <Badge tone="soft">High Priority</Badge>
                      )}
                    </div>
                  </div>
                  <PointsBadge points={task.points} size="lg" />
                </div>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground mb-0.5">Difficulty</dt>
                    <dd className="text-foreground font-semibold">
                      {getDifficultyLabel(task.points)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-0.5">Points</dt>
                    <dd className="text-foreground font-semibold">
                      {task.points} point{task.points > 1 ? "s" : ""}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-0.5">Last Completed</dt>
                    <dd className="text-foreground font-semibold">
                      {task.last_completed_at
                        ? new Date(task.last_completed_at).toLocaleDateString(
                            "en-US",
                            {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                            }
                          )
                        : "Never"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm text-muted-foreground mb-0.5">Area</dt>
                    <dd className="text-foreground font-semibold">
                      {taskArea?.name || "Unknown"}
                    </dd>
                  </div>
                </dl>

                {/* Additional Details */}
                <div className="mt-5 pt-5 border-t border-border">
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-muted-foreground mb-0.5">Created</dt>
                      <dd className="text-foreground font-semibold">
                        {formatShortDate(task.created_at)}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-muted-foreground mb-0.5">Last Updated</dt>
                      <dd className="text-foreground font-semibold">
                        {formatShortDate(task.updated_at)}
                      </dd>
                    </div>
                  </dl>
                  {task.description && (
                    <div className="mt-4">
                      <div className="text-sm text-muted-foreground mb-0.5">Description</div>
                      <div className="text-foreground">{task.description}</div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Complete Task Button */}
              {isCompletedToday ? (
                <div
                  role="status"
                  className="w-full min-h-[56px] flex items-center justify-center gap-2 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 font-semibold"
                >
                  <CheckCircle2 className="w-5 h-5" aria-hidden="true" />
                  Completed today
                </div>
              ) : (
                <PrimaryButton
                  onClick={() => setShowCompleteModal(true)}
                  leadingIcon={<Check className="w-5 h-5" aria-hidden="true" />}
                >
                  Complete Task
                </PrimaryButton>
              )}

              {/* Completion History */}
              <section aria-labelledby="completion-history-heading">
                <h2 id="completion-history-heading" className="section-title mb-3">
                  Completion History
                </h2>
                {taskHistory.length > 0 ? (
                  <ul className="card-surface divide-y divide-border overflow-hidden">
                    {taskHistory.map((completion: ITaskCompletion) => (
                      <li key={completion.uuid} className="flex items-start gap-3 p-4">
                        <span
                          aria-hidden="true"
                          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 shrink-0"
                        >
                          <Check className="w-4 h-4" strokeWidth={3} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-foreground font-semibold">
                            {completion.completed_at
                              ? new Date(completion.completed_at).toLocaleDateString(
                                  "en-US",
                                  {
                                    weekday: "long",
                                    month: "long",
                                    day: "numeric",
                                    year: "numeric",
                                  }
                                )
                              : "Pending"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {completion.completed_at
                              ? new Date(completion.completed_at).toLocaleTimeString(
                                  "en-US",
                                  { hour: "numeric", minute: "2-digit" }
                                )
                              : ""}
                          </div>
                          {completion.notes && (
                            <div className="mt-2 text-sm text-foreground/80">
                              {completion.notes}
                            </div>
                          )}
                        </div>
                        <Badge tone="success">+{completion.points_earned} pts</Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="rounded-3xl bg-surface p-8 text-center">
                    <ClipboardList
                      className="w-10 h-10 text-muted-foreground mx-auto mb-3"
                      aria-hidden="true"
                    />
                    <div className="text-muted-foreground font-medium">
                      No completions yet
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}

          {/* Delete button (only in edit mode) */}
          {isEditMode && (
            <div className="mt-8 pt-8 border-t border-border">
              <Button
                variant="danger"
                block
                size="lg"
                leadingIcon={<Trash2 className="w-4 h-4" aria-hidden="true" />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Task
              </Button>
            </div>
          )}

          {showDeleteConfirm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-6 z-50">
              <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="delete-task-title"
                className="bg-card rounded-3xl p-6 max-w-sm w-full shadow-sheet"
              >
                <h3 id="delete-task-title" className="text-lg font-extrabold text-foreground mb-2">
                  Delete Task?
                </h3>
                <p className="text-muted-foreground mb-6">
                  This will permanently delete "{task.name}" and all its
                  completion history.
                </p>
                <div className="flex gap-3">
                  <Button
                    ref={deleteDialogFocusRef}
                    variant="secondary"
                    className="flex-1"
                    onClick={closeDeleteConfirm}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDelete}
                    loading={isDeleting}
                    className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-none"
                  >
                    {isDeleting ? "Deleting..." : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Complete Task Modal */}
          {showCompleteModal && (
            <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center sm:p-6 z-50">
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="complete-task-title"
                className="bg-card rounded-t-[28px] sm:rounded-3xl p-6 pb-[calc(env(safe-area-inset-bottom)+24px)] max-w-md w-full shadow-sheet"
              >
                <h3 id="complete-task-title" className="text-xl font-extrabold tracking-tight text-foreground mb-4">
                  Complete Task
                </h3>

                <div className="flex items-center gap-3 rounded-2xl bg-surface p-4 mb-5">
                  <RoomIcon name={taskArea?.name} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="text-foreground font-semibold truncate">{task.name}</div>
                    <div className="text-sm text-muted-foreground truncate">
                      {taskArea?.name} • Every {task.frequency_number} {task.frequency_unit}
                    </div>
                  </div>
                  <Badge tone="success">+{task.points} pts</Badge>
                </div>

                <div className="mb-4">
                  <label htmlFor="completion-date" className="field-label">
                    Completion Date
                  </label>
                  <input
                    ref={completeDialogFocusRef}
                    id="completion-date"
                    type="date"
                    value={completionDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setCompletionDate(e.target.value)}
                    className="input-field"
                    aria-describedby="completion-date-hint"
                  />
                  <p id="completion-date-hint" className="text-xs text-muted-foreground mt-1">
                    Leave blank for today
                  </p>
                </div>

                <div className="mb-6">
                  <label htmlFor="completion-notes" className="field-label">
                    Notes (Optional)
                  </label>
                  <textarea
                    id="completion-notes"
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    className="input-field resize-none"
                    placeholder="Add any notes about this task completion..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button variant="secondary" className="flex-1" onClick={closeCompleteModal}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={handleConfirmComplete}
                    disabled={isCompleting}
                  >
                    {isCompleting ? "Completing..." : "Complete"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
